// Capacidades reais da Graph API por plataforma/formato. Espelhado no backend
// em backend/src/controllers/v2/marketing/mkt.posts.controller.js (o backend
// nunca confia nesta validacao do cliente, ele reaplica a mesma matriz).
export const PLATFORM_CAPS = {
    facebook: {
        labelKey: 'posts.platforms.facebook',
        icon: 'mdi:facebook',
        color: 'text-blue-600',
        dot: 'bg-blue-500',
        postTypes: { feed: true, carousel: true, story: false, reel: false },
        // 'text-only': o Graph so permite editar a mensagem de um post publicado, nao a midia
        edit: 'text-only',
        delete: true,
        captionByType: { feed: true, carousel: true, story: false, reel: false },
        maxChars: 63206,
        maxCarouselItems: 10
    },
    instagram: {
        labelKey: 'posts.platforms.instagram',
        icon: 'mdi:instagram',
        color: 'text-pink-600',
        dot: 'bg-pink-500',
        postTypes: { feed: true, carousel: true, story: true, reel: true },
        edit: false,
        delete: false,
        captionByType: { feed: true, carousel: true, story: false, reel: true },
        maxChars: 2200,
        maxHashtags: 30,
        maxCarouselItems: 10
    }
};

export const PLATFORM_OPTIONS = [
    { id: 'instagram', ...PLATFORM_CAPS.instagram },
    { id: 'facebook', ...PLATFORM_CAPS.facebook }
];

export const POST_TYPE_OPTIONS = [
    { value: 'feed', labelKey: 'posts.types.feed' },
    { value: 'story', labelKey: 'posts.types.story' },
    { value: 'reel', labelKey: 'posts.types.reel' },
    { value: 'carousel', labelKey: 'posts.types.carousel' }
];

const STATUS_I18N_SUFFIX = {
    partially_published: 'partiallyPublished'
};

export const STATUS_MAP = {
    draft: { labelKey: 'posts.status.draft', dot: 'bg-gray-400', tone: 'gray' },
    scheduled: { labelKey: 'posts.status.scheduled', dot: 'bg-blue-500', tone: 'blue' },
    publishing: { labelKey: 'posts.status.publishing', dot: 'bg-amber-500', tone: 'amber' },
    published: { labelKey: 'posts.status.published', dot: 'bg-green-500', tone: 'green' },
    partially_published: { labelKey: 'posts.status.partiallyPublished', dot: 'bg-amber-500', tone: 'amber' },
    failed: { labelKey: 'posts.status.failed', dot: 'bg-red-500', tone: 'red' },
    cancelled: { labelKey: 'posts.status.cancelled', dot: 'bg-gray-400', tone: 'gray' }
};

export function statusI18nKey(status) {
    const suffix = STATUS_I18N_SUFFIX[status] || status;
    return `posts.status.${suffix}`;
}

export function getStatusLabel(status, t) {
    const cfg = STATUS_MAP[status] || STATUS_MAP.draft;
    return t(cfg.labelKey);
}

export function getPostTypeLabel(postType, t) {
    return t(`posts.types.${postType}`);
}

export function getPlatformLabel(platform, t) {
    const caps = PLATFORM_CAPS[platform];
    if (!caps?.labelKey) return platform;
    return t(caps.labelKey);
}

export const PUBLISH_SCOPES = {
    facebook: ['pages_manage_posts'],
    instagram: ['instagram_content_publish']
};

export function platformsMissingPostType(platforms, postType) {
    return (platforms || []).filter((p) => !PLATFORM_CAPS[p]?.postTypes?.[postType]);
}

export function getMaxChars(platforms) {
    const limits = (platforms || []).map((p) => PLATFORM_CAPS[p]?.maxChars).filter(Boolean);
    return limits.length ? Math.min(...limits) : PLATFORM_CAPS.instagram.maxChars;
}

export function parseHashtags(raw) {
    if (!raw) return [];
    return raw
        .split(/[\s,]+/)
        .map((h) => h.trim())
        .filter(Boolean)
        .map((h) => (h.startsWith('#') ? h : `#${h}`));
}

export function hashtagsToText(hashtags) {
    return (hashtags || []).join(' ');
}

export function resolveValidationMessage(item, t) {
    if (typeof item === 'string') return t(`posts.validation.${item}`);
    return t(`posts.validation.${item.key}`, item.params || {});
}

// Validacao client-side: espelha (nao substitui) a validacao do backend.
// errors bloqueiam o envio; warnings aparecem no preview mas nao bloqueiam.
// Retorna keys de i18n — traduzir com resolveValidationMessage(item, t).
export function validatePost({ platforms, postType, text, media, hashtags }, t) {
    const errors = [];
    const warnings = [];

    const pushError = (key, params) => errors.push(params ? { key, params } : key);
    const pushWarning = (key, params) => warnings.push(params ? { key, params } : key);

    if (!platforms || platforms.length === 0) {
        pushError('platformRequired');
        return { errors, warnings };
    }

    platformsMissingPostType(platforms, postType).forEach((p) => {
        pushError('platformUnsupportedFormat', {
            platform: t ? getPlatformLabel(p, t) : p,
            postType: t ? getPostTypeLabel(postType, t) : postType
        });
    });

    if (platforms.includes('instagram') && (!media || media.length === 0)) {
        pushError('instagramMediaRequired');
    }

    if (postType === 'story' && media && media.length > 1) {
        pushError('storySingleMedia');
    }

    if (postType === 'carousel' && media && media.length > 0 && media.length < 2) {
        pushError('carouselMinImages');
    }

    const maxCarousel = Math.min(...platforms.map((p) => PLATFORM_CAPS[p]?.maxCarouselItems || 10));
    if (postType === 'carousel' && media && media.length > maxCarousel) {
        pushError('carouselMaxMedia', { max: maxCarousel });
    }

    if (!text?.trim() && (!media || media.length === 0)) {
        pushError('contentOrMediaRequired');
    }

    const maxChars = getMaxChars(platforms);
    if (text && text.length > maxChars) {
        pushError('textTooLong', { max: maxChars });
    }

    if (platforms.includes('instagram') && hashtags && hashtags.length > PLATFORM_CAPS.instagram.maxHashtags) {
        pushWarning('instagramMaxHashtags', { max: PLATFORM_CAPS.instagram.maxHashtags });
    }

    if (postType === 'story' && text?.trim()) {
        pushWarning('storyNoCaption');
    }

    if (media?.some((m) => m.type === 'image' && /image\/png/i.test(m.mime || ''))) {
        pushWarning('pngWarning');
    }

    return { errors, warnings };
}
